import React, { FC, useState, useCallback, useEffect } from "react";

import {
    useUpdateAvailabilityMutation,
    useGetAvailabilityPageSettingsQuery,
    useGetHandlerQuery,
    useGetAppLimitationsQuery,
    FormWidgetAppChannel,
} from "generated/graphql";
import { useWrapUrql } from "utils/graphql/useWrapUrql";
import { prepareDataGQL } from "utils/graphql/data";
import { cleanObj } from "utils/Json";

import { DataCollectionStep, DataCollectionStepProps } from "../../components/setup/DataCollectionStep";
import { ClientApp } from "models/App";
import { ContactCaptureData } from "@xapp/contact-capture-handler";

export interface DataCollectionStepContainerProps extends Omit<DataCollectionStepProps, "onNext" | "app"> {
    app?: Pick<ClientApp, "appId" | "channels">;
    onNext(): void;
    onBack?(): void;
}

export const DataCollectionStepContainer: FC<DataCollectionStepContainerProps> = (props) => {
    const { app, onNext, onBack, ...restProps } = props;

    const [saving, setSaving] = useState(false);
    const [handlerId, setHandlerId] = useState<string>();

    // get the normal data
    const [useAvailabilityPageSettings] = useGetAvailabilityPageSettingsQuery({
        pause: !app?.appId,
        requestPolicy: "cache-and-network",
        variables: {
            appId: app?.appId,
        },
    });

    const { fetching: availabilityFetching, data: availabilityData } = useAvailabilityPageSettings;

    const appData = availabilityData?.app;

    const formChannel = appData?.channels?.find((channel) => channel?.type === "form-widget") as
        | FormWidgetAppChannel
        | undefined;

    useEffect(() => {
        if (formChannel?.intentId) {
            setHandlerId(formChannel.intentId);
        }
    }, [formChannel]);

    // Get the handler data to pre-populate service options
    const [getHandler] = useGetHandlerQuery({
        pause: !handlerId || !app?.appId,
        requestPolicy: "cache-and-network",
        variables: {
            appId: app?.appId,
            intentId: handlerId,
        },
    });

    const { data } = getHandler;

    const handlerData: ContactCaptureData = data?.handler?.data;

    // Email capture is gated on the tier's captureLead entitlement.
    const [getLimitations] = useGetAppLimitationsQuery({
        pause: !app?.appId,
        requestPolicy: "cache-and-network",
        variables: { appId: app?.appId },
    });
    const emailCaptureAvailable = getLimitations.data?.app?.limitations?.captureLead ?? true;

    // Setup updateAvailability mutation (updates both app and handler)
    const updateAvailability = useWrapUrql(useUpdateAvailabilityMutation(), {
        onSuccessMessage: false,
    });

    const handleNext = useCallback(
        async (data: ContactCaptureData) => {
            setSaving(true);

            try {
                // Transform service options and email capture for the handler data.
                // capture.data is the key the contact-capture-handler consumes.
                const handlerData: Record<string, unknown> = {
                    captureLead: data.captureLead ?? true,
                    capture: {
                        serviceOptions: data.capture.serviceOptions.map((option) => ({
                            id: option.id,
                            label: option.label,
                            requiresDate: option.requiresDate,
                        })),
                        ...(data.capture.data ? { data: data.capture.data } : {}),
                    },
                };

                // Only update if we have a handler ID (form widget channel exists)
                if (handlerId && app?.appId) {
                    await updateAvailability({
                        appId: app.appId,
                        app: {}, // Empty object since no app updates needed for this step, but parameter is non-null
                        handlerId,
                        handler: prepareDataGQL({
                            name: "LeadGeneration",
                            data: cleanObj(handlerData),
                        }),
                    });
                }

                // Success - proceed to next step
                onNext();
            } catch (err) {
                console.error("Failed to update data collection configuration:", err);
                // Still proceed to next step even if save fails - we don't want to block the user
                onNext();
            } finally {
                setSaving(false);
            }
        },
        [onNext, app?.appId, handlerId, updateAvailability]
    );

    return (
        <DataCollectionStep
            {...restProps}
            value={handlerData}
            emailCaptureAvailable={emailCaptureAvailable}
            onNext={handleNext}
            onBack={onBack}
            saving={saving}
            loading={!handlerId || !app?.appId || availabilityFetching || getHandler.fetching || getHandler.stale}
        />
    );
};
